/**
 * =============================================================================
 * API Service
 * =============================================================================
 *
 * Axios-based HTTP client for Admin Portal API communication.
 *
 * Features:
 * - Automatic JWT token injection
 * - Token refresh on 401 responses
 * - Request/response interceptors
 * - Typed API methods
 *
 * Base URL is configured via environment variable or defaults to /api
 * (handled by Vite proxy in development).
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import keycloak, { refreshToken } from '../auth/keycloak'
import { clearLocalSession, getLocalAccessToken } from '../auth/localAuth'
import { notifySessionExpired } from '../auth/sessionStore'
import { deriveHealthUrl } from '../utils/apiHealth'

// =============================================================================
// Types
// =============================================================================

/**
 * Standard API error response structure.
 */
export interface ApiError {
  detail: string
  type?: string
}

export interface AuthUser {
  id: string
  username: string
  email?: string
  first_name?: string
  last_name?: string
  roles: string[]
  auth_provider: 'local' | 'keycloak'
  must_change_password?: boolean
}

export interface LocalLoginResponse {
  access_token: string
  token_type: string
  expires_in: number
  user: AuthUser
}

export interface LocalUser {
  id: string
  username: string
  email?: string | null
  first_name?: string | null
  last_name?: string | null
  roles: string[]
  is_active: boolean
  must_change_password: boolean
  last_login_at?: string | null
  created_at: string
  updated_at: string
  created_by?: string | null
}

export interface UnifiedUser {
  id: string
  username: string
  email?: string | null
  first_name?: string | null
  last_name?: string | null
  roles: string[]
  is_active: boolean
  auth_provider: 'local' | 'keycloak' | 'clinic'
  must_change_password: boolean
  institution_id?: string | null
  institution_name?: string | null
  last_login_at?: string | null
}

// =============================================================================
// Axios Instance
// =============================================================================

/**
 * Base API URL from runtime config (preferred), then build env.
 *
 * Local development defaults to relative /api/v1 to use Vite proxy and avoid
 * cross-origin CORS mismatches between localhost and 127.0.0.1.
 *
 * VITE_API_BASE_URL is kept for production build compatibility.
 */
const ENV_API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD ? import.meta.env.VITE_API_BASE_URL : undefined)

const API_BASE_URL =
  window.__APP_CONFIG__?.API_BASE_URL ||
  ENV_API_BASE_URL ||
  '/api/v1'

/**
 * Axios instance with default configuration.
 */
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
})

// =============================================================================
// Request Interceptor
// =============================================================================

/**
 * Request interceptor: Add JWT token to all requests.
 */
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const localToken = getLocalAccessToken()
    if (localToken) {
      config.headers.Authorization = `Bearer ${localToken}`
      return config
    }

    // Ensure token is fresh
    if (keycloak.authenticated) {
      try {
        await refreshToken(30) // Refresh if less than 30s validity
      } catch (error) {
        console.warn('Token refresh failed:', error)
      }
    }

    // Add token to headers
    if (keycloak.token) {
      config.headers.Authorization = `Bearer ${keycloak.token}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// =============================================================================
// Response Interceptor
// =============================================================================

/**
 * Response interceptor: Handle common error scenarios.
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config
    const localToken = getLocalAccessToken()
    const requestUrl = originalRequest?.url || ''
    const isLocalLoginRequest = requestUrl.includes('/auth/local/login')

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && originalRequest) {
      // Local login must never trigger Keycloak redirect.
      if (isLocalLoginRequest) {
        return Promise.reject(error)
      }

      if (localToken) {
        // FE issue #24: never hard-redirect. `window.location.href = '/'`
        // reloaded the SPA, which is exactly what made an expired session look
        // like a random jump to the login screen. Announce it instead — the
        // AuthProvider clears the session and the login page explains why.
        clearLocalSession()
        notifySessionExpired('expired')
        return Promise.reject(error)
      }

      // If Keycloak session is not active, do not force redirect.
      if (!keycloak.authenticated) {
        return Promise.reject(error)
      }

      try {
        // Try to refresh token
        const refreshed = await refreshToken(0)

        if (refreshed && keycloak.token) {
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${keycloak.token}`
          return api(originalRequest)
        }
        // Refresh returned "not refreshed" -> the Keycloak session is gone.
        notifySessionExpired('expired')
      } catch {
        // Token refresh failed: the session is over. Say so instead of
        // bouncing straight into the Keycloak login form (FE issue #24).
        console.error('Token refresh failed, session ended')
        notifySessionExpired('expired')
      }
    }

    // Log other errors
    if (error.response) {
      console.error('API Error:', {
        status: error.response.status,
        data: error.response.data,
        url: originalRequest?.url,
      })
    }

    return Promise.reject(error)
  }
)

export default api

// =============================================================================
// API Methods - Authentication
// =============================================================================

export const authApi = {
  localLogin: (username: string, password: string) =>
    api.post<LocalLoginResponse>('/auth/local/login', { username, password }),

  me: () => api.get<AuthUser>('/auth/me'),
}

// =============================================================================
// API Methods - Local Users
// =============================================================================

export interface LocalUserCreate {
  username: string
  email?: string | null
  first_name?: string | null
  last_name?: string | null
  password: string
  roles: string[]
  is_active: boolean
}

export interface LocalUserUpdate {
  username?: string
  email?: string | null
  first_name?: string | null
  last_name?: string | null
  roles?: string[]
  is_active?: boolean
  must_change_password?: boolean
}

export interface LocalUserResetPassword {
  new_password: string
  must_change_password: boolean
}

export const localUsersApi = {
  list: (params?: { is_active?: boolean; search?: string }) =>
    api.get<LocalUser[]>('/local-users', { params }),

  listCombined: (params?: {
    is_active?: boolean
    search?: string
    auth_provider?: 'local' | 'keycloak' | 'clinic'
    institution_id?: string
  }) =>
    api.get<UnifiedUser[]>('/local-users/combined', { params }),

  get: (id: string) => api.get<LocalUser>(`/local-users/${id}`),

  create: (payload: LocalUserCreate) => api.post<LocalUser>('/local-users', payload),

  update: (id: string, payload: LocalUserUpdate) =>
    api.put<LocalUser>(`/local-users/${id}`, payload),

  resetPassword: (id: string, payload: LocalUserResetPassword) =>
    api.post<LocalUser>(`/local-users/${id}/reset-password`, payload),
}

// =============================================================================
// API Methods - Institution Users (doctors / institution admins)
// =============================================================================

/**
 * Institution-scoped user roles managed via /users.
 */
export type InstitutionUserRole = 'doctor' | 'institution_admin'

/** Preferred e-mail/UI language of an institution user (BE #46, users.language). */
export type InstitutionUserLanguage = 'sk' | 'en'

/**
 * Institution user (doctor / institution_admin) managed locally in the
 * admin portal DB (BE: app/api/v1/users.py).
 */
export interface InstitutionUser {
  id: string
  email: string
  first_name: string
  last_name: string
  role: InstitutionUserRole | string
  institution_id?: string | null
  institution_name?: string | null
  is_active: boolean
  /** Preferred e-mail/UI language (BE #46); older rows default to 'sk'. */
  language?: InstitutionUserLanguage | string
  keycloak_user_id?: string | null
  must_change_password?: boolean | null
  created_at?: string
  /** Not exposed by the list endpoint yet; kept optional for forward compat. */
  last_login_at?: string | null
  /**
   * Whether an invitation email was sent (#14 / BE #31). Only set by the
   * create endpoint (when no initial password is given); the list endpoint
   * does not expose any invite/password state.
   */
  inviteSent?: boolean | null
  /** Local password set (BE #32); false = invited, not activated yet. */
  has_password?: boolean
  /** Invitation state (BE #61). */
  invite_status?: 'active' | 'pending' | 'expired' | 'not_delivered' | 'not_invited' | null
  invite_sent_at?: string | null
  invite_expires_at?: string | null
  /** Mail triggered by this request (BE #61): invite | assignment. */
  notification?: 'invite' | 'assignment' | null
  mail_status?: 'sent' | 'disabled' | 'failed' | null
  mail_error?: string | null
}

/** Response of POST /users/{id}/resend-invite (#14 / BE #31). */
export interface InstitutionUserInviteResend {
  user_id: string
  inviteSent: boolean
  mail_status?: 'sent' | 'disabled' | 'failed' | null
  mail_error?: string | null
}

export interface InstitutionUserCreate {
  email: string
  first_name: string
  last_name: string
  institution_id: string
  role: InstitutionUserRole
  is_active?: boolean
  /** Invite/e-mail language (BE #46); BE defaults to 'sk' when omitted. */
  language?: InstitutionUserLanguage
  /** Optional initial password (min 8 chars); shown to the admin only once. */
  initialPassword?: string
}

export interface InstitutionUserUpdate {
  first_name?: string
  last_name?: string
  institution_id?: string
  role?: InstitutionUserRole
  is_active?: boolean
}

export interface InstitutionUserStatusUpdate {
  status: 'active' | 'inactive'
}

export interface InstitutionUserSetPassword {
  password: string
}

/**
 * Institution users API methods.
 *
 * NOTE: collection endpoints require the trailing slash ('/users/'); without
 * it the backend responds with a 307 redirect that drops the Authorization
 * header in some proxies.
 */
export const institutionUsersApi = {
  /** List institution users (institution_admin sees only their own) */
  list: (params?: {
    institution_id?: string
    role?: string
    is_active?: boolean
    search?: string
  }) => api.get<InstitutionUser[]>('/users/', { params }),

  /** Get single institution user */
  get: (id: string) => api.get<InstitutionUser>(`/users/${id}`),

  /** Create institution user (optionally with an initial password) */
  create: (payload: InstitutionUserCreate) =>
    api.post<InstitutionUser>('/users/', payload),

  /** Update name / role / institution */
  update: (id: string, payload: InstitutionUserUpdate) =>
    api.put<InstitutionUser>(`/users/${id}`, payload),

  /** Activate / deactivate */
  updateStatus: (id: string, payload: InstitutionUserStatusUpdate) =>
    api.patch<InstitutionUser>(`/users/${id}/status`, payload),

  /** Set / reset login password (never returned by the API) */
  setPassword: (id: string, payload: InstitutionUserSetPassword) =>
    api.post<InstitutionUser>(`/users/${id}/set-password`, payload),

  /** Re-send the Clinic Portal invitation email (#14 / BE #31) */
  resendInvite: (id: string) =>
    api.post<InstitutionUserInviteResend>(`/users/${id}/resend-invite`),
}

// =============================================================================
// API Methods - Institutions
// =============================================================================

/**
 * Institution data types.
 */
export interface Institution {
  id: string
  code: string
  institution_name: string
  institution_type?: string | null
  address?: string | null
  country?: string | null
  login_email: string
  notification_email?: string | null
  status: 'active' | 'inactive'
  legacy_keycloak_group_id?: string | null
  legacy_keycloak_group_path?: string | null
  devices_count?: number
  device_type_breakdown?: Record<string, number>
  device_status_breakdown?: Record<string, number>
  users_count?: number
  user_role_breakdown?: Record<string, number>
  created_at?: string
  updated_at?: string
  /** Outcome of the first-admin provisioning (BE #61, create response only). */
  first_admin?: InstitutionAdminResult | null
}

/** Provision an institution admin (BE #61). */
export interface InstitutionAdminProvision {
  create?: boolean
  /** Defaults to the institution login_email on the BE. */
  email?: string
  first_name?: string
  last_name?: string
  language?: 'sk' | 'en'
  /** Move an existing institution user here instead of a 409. */
  assign_existing?: boolean
}

export interface InstitutionAdminResult {
  action: 'created' | 'assigned'
  user_id: string
  email: string
  role: string
  notification: 'invite' | 'assignment'
  mail_status: 'sent' | 'disabled' | 'failed'
  mail_error?: string | null
  previous_institution_name?: string | null
}

export interface InstitutionCreate {
  code: string
  institution_name: string
  institution_type?: string | null
  address?: string | null
  country?: string | null
  login_email: string
  notification_email?: string | null
  status?: 'active' | 'inactive'
  first_admin?: InstitutionAdminProvision
}

export interface InstitutionUpdate {
  institution_name?: string
  institution_type?: string | null
  address?: string | null
  country?: string | null
  login_email?: string
  notification_email?: string | null
  status?: 'active' | 'inactive'
}

export interface InstitutionStatusUpdate {
  status: 'active' | 'inactive'
}

/**
 * Institution API methods.
 */
export const institutionsApi = {
  /** Get all institutions */
  list: (params?: { status?: string; search?: string }) =>
    api.get<Institution[]>('/institutions/', { params }),

  /** Get single institution by ID */
  get: (id: string) => api.get<Institution>(`/institutions/${id}`),

  /** Create institution */
  create: (payload: InstitutionCreate) => api.post<Institution>('/institutions/', payload),

  /** Update institution */
  update: (id: string, payload: InstitutionUpdate) =>
    api.put<Institution>(`/institutions/${id}`, payload),

  /** Update institution status */
  updateStatus: (id: string, payload: InstitutionStatusUpdate) =>
    api.patch<Institution>(`/institutions/${id}/status`, payload),

  /** Get current user's institution (institution_admin) */
  me: () => api.get<Institution>('/institutions/me'),

  /** Provision an institution admin for an existing institution (BE #61) */
  addAdmin: (id: string, payload: InstitutionAdminProvision) =>
    api.post<InstitutionAdminResult>(`/institutions/${id}/admins`, payload),
}

// =============================================================================
// API Methods - Devices
// =============================================================================

/**
 * Device data types.
 */
export interface Device {
  id: string
  serial_number: string
  device_type: string  // 'physical' | 'virtual'
  institution_id: string
  institution_name: string | null
  status: string  // 'active' | 'maintenance' | 'retired' | 'demo'
  location: string | null
  location_type: string | null  // 'hospital' | 'home' | 'lab'
  notes: string | null                // Device notes/comments
  assigned_patient_id: string | null
  assigned_user_name?: string | null
  assigned_user_provider?: string | null
  last_seen_at: string | null
  created_at: string
  created_by: string | null           // UUID of user who created the device
  created_by_name: string | null      // Name of user who created the device
}

export interface AssignableUser {
  id: string
  username: string
  display_name: string
  email?: string | null
  auth_provider: 'local' | 'keycloak'
  roles: string[]
  institution_id?: string | null
}

export interface DeviceCreate {
  serial_number: string
  device_type: string  // 'physical' | 'virtual'
  institution_id: string
  location?: string
  location_type?: string  // 'hospital' | 'home' | 'lab'
  notes?: string  // Device notes/comments
}

export interface DeviceCreateResponse extends Device {
  api_key: string
}

export interface DeviceUpdate {
  status?: string  // 'active' | 'maintenance' | 'retired' | 'demo'
  location?: string
  location_type?: string  // 'hospital' | 'home' | 'lab'
  institution_id?: string
  notes?: string  // Device notes/comments
}

/**
 * Device API methods.
 */
export const devicesApi = {
  /** Get all devices with optional filters */
  list: (params?: { institution_id?: string; status?: string }) =>
    api.get<Device[]>('/devices/', { params }),

  /** Get single device by ID */
  get: (id: string) => api.get<Device>(`/devices/${id}`),

  /** Create new device */
  create: (data: DeviceCreate) => api.post<DeviceCreateResponse>('/devices/', data),

  /** Update device */
  update: (id: string, data: DeviceUpdate) => api.put<Device>(`/devices/${id}`, data),

  /** Delete virtual device */
  delete: (id: string) => api.delete(`/devices/${id}`),

  /** Assign patient to device */
  assignPatient: (id: string, patientId: string) =>
    api.put<Device>(`/devices/${id}/assign-patient`, {
      patient_id: patientId,
    }),

  assignUser: (id: string, userId: string) =>
    api.put<Device>(`/devices/${id}/assign-patient`, {
      patient_id: userId,
    }),

  listAssignableUsers: (id: string) =>
    api.get<AssignableUser[]>(`/devices/${id}/assignable-users`),

  /** Unassign patient from device */
  unassignPatient: (id: string) => api.delete<Device>(`/devices/${id}/assign-patient`),

  /** Regenerate device API key */
  regenerateApiKey: (id: string) => api.post<DeviceCreateResponse>(`/devices/${id}/regenerate-api-key`),
}

// =============================================================================
// API Methods - Hands
// =============================================================================

/**
 * Hand data types.
 */
export interface Tensometer {
  id: string
  tensometer_id: string
  hand_id: string
  finger_position: string
  status: string
  calibration_offset: number
  calibration_scale: number
  created_at: string
}

export interface TensometerUpdate {
  calibration_offset?: number
  calibration_scale?: number
}

export interface Hand {
  id: string
  hardware_id: string
  hand_type: string  // 'left' | 'right'
  hardware_version: string
  institution_id: string | null
  institution_name: string | null
  manufactured_at: string | null
  manufacturer: string | null
  status: string  // 'active' | 'maintenance' | 'retired'
  last_calibrated_at: string | null
  tensometers: Tensometer[]
  created_at: string
}

export interface HandCreate {
  hardware_id: string
  hand_type: string  // 'left' | 'right'
  hardware_version?: string
  institution_id?: string
  manufactured_at?: string
  manufacturer?: string
}

export interface HandUpdate {
  institution_id?: string
  status?: string  // 'active' | 'maintenance' | 'retired'
  notes?: string
}

export interface Calibration {
  id: string
  hand_hardware_id: string
  adc_offset: number[]
  adc_scale: number[]
  calibration_method: string
  notes: string | null
  calibrated_at: string
  created_at: string
}

export interface CalibrationCreate {
  hand_hardware_id: string
  adc_offset: number[]
  adc_scale: number[]
  calibration_method: string
  notes?: string
}

/**
 * Hand API methods.
 */
export const handsApi = {
  /** Get all hands with optional filters */
  list: (params?: { institution_id?: string; status?: string }) =>
    api.get<Hand[]>('/hands/', { params }),

  /** Get single hand by ID */
  get: (id: string) => api.get<Hand>(`/hands/${id}`),

  /** Create new hand */
  create: (data: HandCreate) => api.post<Hand>('/hands/', data),

  /** Update hand */
  update: (id: string, data: HandUpdate) => api.put<Hand>(`/hands/${id}`, data),

  /** Get tensometers for a hand */
  getTensometers: (handId: string) => api.get<Tensometer[]>(`/hands/${handId}/tensometers`),

  /** Update tensometer calibration values */
  updateTensometer: (handId: string, tensometerId: string, data: TensometerUpdate) =>
    api.put<Tensometer>(`/hands/${handId}/tensometers/${tensometerId}`, data),

  /** Get calibration history for hand */
  getCalibrations: (id: string) => api.get<Calibration[]>(`/hands/${id}/calibrations`),

  /** Create new calibration */
  createCalibration: (data: CalibrationCreate) =>
    api.post<Calibration>('/hands/calibrations', data),

  /** Get latest calibrations for all hands */
  getLatestCalibrations: () => api.get<Calibration[]>('/hands/calibrations/latest'),
}

// =============================================================================
// API Methods - Device Types
// =============================================================================

export interface DeviceTypeInfo {
  id: string
  code: string
  name: string
  description?: string | null
  is_builtin: boolean
  is_active: boolean
}

export interface DeviceType extends DeviceTypeInfo {
  created_at: string
  updated_at: string
}

export interface DeviceTypeCreate {
  code: string
  name: string
  description?: string | null
  is_active?: boolean
}

export interface DeviceTypeUpdate {
  code?: string
  name?: string
  description?: string | null
  is_active?: boolean
}

export const deviceTypesApi = {
  list: (params?: { is_active?: boolean }) =>
    api.get<DeviceType[]>('/device-types', { params }),
  get: (id: string) => api.get<DeviceType>(`/device-types/${id}`),
  create: (payload: DeviceTypeCreate) => api.post<DeviceType>('/device-types', payload),
  update: (id: string, payload: DeviceTypeUpdate) =>
    api.put<DeviceType>(`/device-types/${id}`, payload),
  deactivate: (id: string) => api.delete(`/device-types/${id}`),
}

// =============================================================================
// API Methods - Apps
// =============================================================================

/**
 * App parameter from YAML config.
 */
export interface GameParameter {
  name: string
  type: string  // 'integer' | 'float' | 'boolean' | 'string' | 'enum'
  default: unknown
  min?: number
  max?: number
  values?: string[]  // For enum type
  unit?: string
  description?: string
  doctor_configurable: boolean
  doctor_visibility?: string
  importance?: string  // 'low' | 'medium' | 'high'
  scope?: 'global' | 'app'
}

/**
 * App requirements from YAML config.
 */
export interface GameRequirements {
  min_firmware_version?: string
  max_firmware_version?: string
  min_ram_mb?: number
  min_disk_mb?: number
  requires_imu: boolean
  requires_tensometers: boolean
}

/**
 * App result schema field.
 */
export interface GameResultSchema {
  name: string
  type: string
  required: boolean
  unit?: string
  description?: string
}

/**
 * App list item.
 */
export interface GameListItem {
  id: string
  app_id: string  // Unique app identifier (slug from config)
  game_id?: string  // Alias for backward compatibility
  name: string
  description?: string
  current_version?: number  // Version number (int)
  current_version_id?: string  // UUID of current version
  version?: string  // Alias for backward compatibility
  category?: string  // 'diagnostics' | 'rehabilitation' | 'cognitive' | 'other'
  environment?: string  // 'dev' | 'test' | 'prod'
  status: string  // app status
  version_status?: string  // Status of current version
  app_team_id?: string
  app_team_name?: string  // Team name (replaces developer_name)
  developer_id?: string  // Deprecated
  developer_name?: string  // Deprecated - use app_team_name
  docker_image_url?: string  // Full image reference (image:tag)
  harbor_image?: string  // Alias for backward compatibility
  total_versions?: number  // Deprecated (versioning removed)
  current_image_digest?: string  // Latest digest observed for the image tag
  approved_image_digest?: string  // Digest that was approved
  created_at: string
  submitted_at?: string
  supported_device_types?: DeviceTypeInfo[]
  supported_device_type_ids?: string[]
}

/**
 * App detail.
 * Extends GameListItem which already includes environment field.
 */
export interface GameDetail extends GameListItem {
  developer_organization?: string  // Deprecated
  config_json?: Record<string, unknown>  // Parsed config
  config_data?: Record<string, unknown>  // Alias for backward compatibility
  config_yaml?: string  // Raw YAML config
  config_yaml_raw?: string  // Alias for backward compatibility
  config_version?: string
  parameters: GameParameter[]
  requirements?: GameRequirements
  result_schema: GameResultSchema[]
  updated_at: string
  reviewed_at?: string
  reviewed_by?: string
  reviewed_by_name?: string
  review_comment?: string  // Review comment
  rejection_reason?: string  // Alias for backward compatibility
}

/**
 * App create request.
 */
export interface GameCreate {
  name: string
  description?: string
  app_team_id: string  // UUID of the team that owns this app
  category: string  // 'diagnostics' | 'rehabilitation' | 'cognitive' | 'other'
  environment?: string  // Deprecated: approval is uniform now (no per-env auto-approve)
  docker_image_url: string  // Full image reference, e.g. host/project/name:test-latest
  supported_device_type_ids: string[]
  config_yaml?: string  // Optional YAML content (stored as-is)
  app_parameters_yaml?: string  // Optional parameters-only YAML
}

/**
 * App update request.
 */
export interface GameUpdate {
  name?: string
  description?: string
  category?: string
  environment?: string  // 'dev' | 'test' | 'prod'
  docker_image_url?: string
  config_yaml?: string
}

/**
 * App review/comment.
 */
export interface GameReview {
  id: string
  game_id: string
  version_id?: string
  version_number?: number
  reviewer_id: string
  reviewer_name: string
  comment: string
  review_type?: string  // Frontend sends this
  status?: string  // Backend returns this: 'comment' | 'approved' | 'rejected' | 'request_changes'
  created_at: string
}

/**
 * App review create request.
 */
export interface GameReviewCreate {
  comment: string
  review_type: string  // 'comment' | 'request_changes'
}

/**
 * App reject request.
 */
export interface GameRejectRequest {
  reason: string
  comment?: string
}

/**
 * App version.
 */
export interface AppVersion {
  id: string
  app_id: string
  version: number
  docker_image_url: string
  config_yaml?: string
  config_json?: Record<string, unknown>
  status: string  // 'draft' | 'pending_approval' | 'approved' | 'rejected'
  submitted_at?: string
  submitted_by?: string
  reviewed_at?: string
  reviewed_by?: string
  review_comment?: string
  created_at: string
}

/**
 * App version create request.
 */
export interface AppVersionCreate {
  docker_image_url: string
  config_yaml: string
  app_parameters_yaml?: string
}

/**
 * App statistics.
 */
export interface GameStats {
  total_games: number
  draft_games: number
  pending_approval: number
  approved_games: number
  rejected_games: number
  deprecated_games: number
  // By environment
  dev_games: number
  test_games: number
  prod_games: number
}

/**
 * App category.
 */
export interface GameCategory {
  id: string
  name: string
  display_name: string
  description?: string
  icon?: string
  color?: string
  is_active: boolean
  sort_order: number
  games_count: number
  created_at: string
  updated_at: string
}

/**
 * App category create request.
 */
export interface GameCategoryCreate {
  name: string
  display_name: string
  description?: string
  icon?: string
  color?: string
  sort_order?: number
}

/**
 * App category update request.
 */
export interface GameCategoryUpdate {
  display_name?: string
  description?: string
  icon?: string
  color?: string
  sort_order?: number
  is_active?: boolean
}

/**
 * Apps API methods (formerly Games).
 */
export const gamesApi = {
  /** Get all apps (filtered by role) */
  list: (params?: { status?: string; environment?: string; app_team_id?: string; device_type_id?: string }) =>
    api.get<GameListItem[]>('/apps/', { params }),

  /** Get apps pending approval (admin only) */
  listPending: () => api.get<GameListItem[]>('/apps/pending'),

  /** Get app statistics (admin only) */
  getStats: () => api.get<GameStats>('/apps/stats'),

  /** Get single app by ID */
  get: (id: string) => api.get<GameDetail>(`/apps/${id}`),

  /** Create new app */
  create: (data: GameCreate) => api.post<GameDetail>('/apps/', data),

  /** Update app (only in draft/rejected status) */
  update: (id: string, data: GameUpdate) => api.put<GameDetail>(`/apps/${id}`, data),

  /** Delete app */
  delete: (id: string) => api.delete(`/apps/${id}`),

  /** Submit app for approval */
  submit: (id: string) => api.post<GameDetail>(`/apps/${id}/submit`),

  /** Approve app (admin only) */
  approve: (id: string, comment?: string) =>
    api.post<GameDetail>(`/apps/${id}/approve`, comment ? { comment } : {}),

  /** Reject app (admin only) */
  reject: (id: string, data: GameRejectRequest) =>
    api.post<GameDetail>(`/apps/${id}/reject`, data),

  /** Get app reviews */
  getReviews: (id: string) => api.get<GameReview[]>(`/apps/${id}/reviews`),

  /** Add app review (admin only) */
  addReview: (id: string, data: GameReviewCreate) =>
    api.post<GameReview>(`/apps/${id}/reviews`, data),

  // ==========================================================================
  // Categories
  // ==========================================================================

  /** Get all app categories */
  listCategories: () => api.get<GameCategory[]>('/apps/categories'),

  /** Get single category by ID */
  getCategory: (id: string) => api.get<GameCategory>(`/apps/categories/${id}`),

  /** Create new category (admin only) */
  createCategory: (data: GameCategoryCreate) =>
    api.post<GameCategory>('/apps/categories', data),

  /** Update category (admin only) */
  updateCategory: (id: string, data: GameCategoryUpdate) =>
    api.put<GameCategory>(`/apps/categories/${id}`, data),

  /** Delete category (admin only) */
  deleteCategory: (id: string) => api.delete(`/apps/categories/${id}`),
}

// =============================================================================
// API Methods - App Teams
// =============================================================================

/**
 * Harbor credentials for a team.
 */
export interface HarborCredentials {
  registry_url: string
  project_name: string
  robot_account: string
  robot_secret: string
}

/**
 * App Team member info.
 */
export interface AppTeamMember {
  id?: string
  member_source: 'local' | 'keycloak'
  user_id: string
  username?: string
  email?: string
  first_name?: string
  last_name?: string
  display_name?: string
  role_in_team?: string
  roles?: string[]
}

/**
 * App Team list item.
 */
export interface AppTeam {
  id: string
  keycloak_group_id?: string | null
  keycloak_group_path?: string | null
  name: string
  team_email?: string
  app_limit: number
  status: 'active' | 'inactive'
  harbor_project_name?: string | null
  member_count: number
  app_count: number
  created_at?: string
  updated_at?: string
}

/**
 * App Team create response with Harbor credentials.
 */
export interface AppTeamCreateResponse extends AppTeam {
  harbor_credentials: HarborCredentials
}

/**
 * App Team create request.
 */
export interface AppTeamCreate {
  name: string
  team_email?: string
  app_limit?: number
}

/**
 * App Team update request.
 */
export interface AppTeamUpdate {
  name?: string
  team_email?: string
  app_limit?: number
}

export interface AppTeamMemberCreate {
  member_source: 'local' | 'keycloak'
  local_user_id?: string
  keycloak_user_id?: string
}

/**
 * App Teams API methods.
 */
export const appTeamsApi = {
  /** Get all app teams */
  list: (params?: { status?: string }) =>
    api.get<AppTeam[]>('/app-teams', { params }),

  /** Get current user's teams */
  getMyTeams: () => api.get<AppTeam[]>('/app-teams/my-teams'),

  /** Get single team by ID */
  get: (id: string) => api.get<AppTeam>(`/app-teams/${id}`),

  /** Get team members */
  getMembers: (id: string) => api.get<AppTeamMember[]>(`/app-teams/${id}/members`),

  /** Add team member */
  addMember: (id: string, payload: AppTeamMemberCreate) =>
    api.post<AppTeamMember>(`/app-teams/${id}/members`, payload),

  /** Remove team member */
  removeMember: (id: string, memberId: string) =>
    api.delete(`/app-teams/${id}/members/${memberId}`),

  /** Create new team */
  create: (data: AppTeamCreate) => api.post<AppTeamCreateResponse>('/app-teams', data),

  /** Update team */
  update: (id: string, data: AppTeamUpdate) => api.put<AppTeam>(`/app-teams/${id}`, data),

  /** Deactivate team */
  deactivate: (id: string) => api.post(`/app-teams/${id}/deactivate`),

  /** Activate team */
  activate: (id: string) => api.post(`/app-teams/${id}/activate`),

  /** Get Harbor credentials for team */
  getCredentials: (id: string) => api.get<HarborCredentials>(`/app-teams/${id}/credentials`),
}

// =============================================================================
// API Methods - Apps (new naming, uses /apps endpoint)
// =============================================================================

/**
 * App list item.
 */
export interface AppListItem {
  id: string
  app_id: string
  name: string
  description?: string
  /** Approval workflow: draft | pending_approval | approved | rejected */
  status: string
  /**
   * Operational switch (BE issue #52) — independent of `status`.
   * false = deactivated: devices do not receive the app, but its approval
   * stays valid so activation is instant.
   */
  is_active?: boolean
  version_status?: string
  category?: string
  environment?: string
  current_version?: number
  docker_image_url?: string
  app_team_id?: string
  app_team_name?: string
  developer_id?: string
  developer_name?: string
  total_versions?: number
  current_image_digest?: string
  approved_image_digest?: string
  created_at: string
  submitted_at?: string
  supported_device_types?: DeviceTypeInfo[]
  supported_device_type_ids?: string[]
}

/**
 * App detail.
 */
export interface AppDetail {
  id: string
  app_id: string  // Unique app identifier (slug)
  name: string
  description?: string
  status: string  // approval workflow: draft | pending_approval | approved | rejected
  /**
   * Operational switch (BE issue #52) — independent of `status`.
   * false = deactivated: devices do not receive the app (device-api gates on
   * the same flag), but the approval and approved_image_digest stay valid, so
   * activation is instant and needs no new review.
   */
  is_active?: boolean
  category?: string
  environment?: string  // 'dev' | 'test' | 'prod'

  // Team info
  app_team_id?: string
  app_team_name?: string

  // Image + config (single mutable row)
  current_version?: number  // Deprecated (versioning removed)
  current_version_id?: string
  docker_image_url?: string
  config_yaml?: string  // YAML config (stored as-is)
  config_json?: Record<string, unknown>
  version_status?: string  // Deprecated alias of status
  current_image_digest?: string  // Latest digest observed for the image tag
  approved_image_digest?: string  // Digest that was approved
  // Issue #50: BE-computed. True when image rebuilds of this app are released
  // automatically by the digest watcher (environment='dev' + AUTO_APPROVE_DEV_APPS).
  // Always false for stage/prod — those still need a human approval.
  auto_approved?: boolean
  supported_device_types?: DeviceTypeInfo[]
  supported_device_type_ids?: string[]

  // Parsed config sections
  parameters: GameParameter[]
  requirements?: GameRequirements
  result_schema: GameResultSchema[]

  // Version counts
  total_versions?: number

  // Timestamps
  created_at: string
  updated_at?: string
  submitted_at?: string
  reviewed_at?: string

  // Review info
  reviewed_by?: string
  reviewed_by_name?: string
  review_comment?: string
}

/**
 * App create request.
 */
export interface AppCreate {
  name: string
  description?: string
  category: string
  environment?: string  // Deprecated: approval is uniform now
  docker_image_url: string  // Full image reference, e.g. host/project/name:test-latest
  app_team_id?: string
  supported_device_type_ids: string[]
  config_yaml?: string  // Optional (stored as-is)
  app_parameters_yaml?: string
}

/**
 * App update request.
 */
export interface AppUpdate {
  name?: string
  description?: string
  category?: string
  environment?: string
  docker_image_url?: string
  config_yaml?: string
  app_parameters_yaml?: string
  supported_device_type_ids?: string[]
}

/**
 * One mutable ("floating") tag of the app's Harbor repository — BE issue #24/#25.
 * Used by the edit form to repoint a pinned `@sha256:` digest in one click.
 */
export interface AppImageTag {
  name: string
  digest?: string | null
  points_to_current: boolean
  pushed_at?: string | null
}

/** Response of `GET /apps/{id}/image-tags`. */
export interface AppImageTags {
  repository: string
  current_reference: string
  is_digest: boolean
  current_digest?: string | null
  harbor_available: boolean
  tags: AppImageTag[]
}

/**
 * App statistics.
 */
export interface AppStats {
  total_apps: number
  draft_apps: number
  pending_approval: number
  approved_apps: number
  rejected_apps: number
  deprecated_apps: number
  dev_apps: number
  test_apps: number
  prod_apps: number
}

/**
 * App review/activity log item.
 */
export interface AppReview {
  id: string
  app_id: string
  version_id?: string
  version_number?: number
  reviewer_id: string
  reviewer_name: string
  comment: string
  review_type?: string
  status?: string  // 'comment' | 'approved' | 'rejected' | 'request_changes'
  created_at: string
}

/**
 * App review create request.
 */
export interface AppReviewCreate {
  comment: string
  review_type: string  // 'comment' | 'request_changes'
}

/**
 * App reject request.
 */
export interface AppRejectRequest {
  reason: string
  comment?: string
}

/**
 * Apps API methods.
 */
export const appsApi = {
  /** Get all apps (filtered by role) */
  list: (params?: {
    status?: string
    environment?: string
    app_team_id?: string
    device_type_id?: string
    /** Operational switch (BE #52): true = active only, false = deactivated only */
    active?: boolean
  }) => api.get<AppListItem[]>('/apps/', { params }),

  /** Get apps pending approval (admin only) */
  listPending: () => api.get<AppListItem[]>('/apps/pending'),

  /** Get app statistics (admin only) */
  getStats: () => api.get<AppStats>('/apps/stats'),

  /** Get single app by ID */
  get: (id: string) => api.get<AppDetail>(`/apps/${id}`),

  /** Create new app */
  create: (data: AppCreate) => api.post<AppDetail>('/apps/', data),

  /** Update app (only in draft/rejected status) */
  update: (id: string, data: AppUpdate) => api.put<AppDetail>(`/apps/${id}`, data),

  /** Delete app */
  delete: (id: string) => api.delete(`/apps/${id}`),

  /** Submit app for approval */
  submit: (id: string) => api.post<AppDetail>(`/apps/${id}/submit`),

  /** Approve app (admin only) */
  approve: (id: string, comment?: string) =>
    api.post<AppDetail>(`/apps/${id}/approve`, comment ? { comment } : {}),

  /** Reject app — review verdict on this version (admin only) */
  reject: (id: string, data: AppRejectRequest) =>
    api.post<AppDetail>(`/apps/${id}/reject`, data),

  /**
   * Deactivate app — operational switch (admin only) — BE issue #52.
   * Devices stop receiving it immediately; the approval stays valid.
   */
  deactivate: (id: string, reason?: string) =>
    api.post<AppDetail>(`/apps/${id}/deactivate`, reason ? { reason } : {}),

  /** Activate a deactivated app (admin only) — BE issue #52 */
  activate: (id: string, comment?: string) =>
    api.post<AppDetail>(`/apps/${id}/activate`, comment ? { comment } : {}),

  /** Get app reviews/activity log */
  getReviews: (id: string) => api.get<AppReview[]>(`/apps/${id}/reviews`),

  /** Add app review/comment */
  addReview: (id: string, data: AppReviewCreate) =>
    api.post<AppReview>(`/apps/${id}/reviews`, data),

  /** Floating image tags of the app's Harbor repository — issue #25 */
  getImageTags: (id: string) => api.get<AppImageTags>(`/apps/${id}/image-tags`),

  /** Get the stored app configuration (raw YAML + parsed JSON) — issue #20 */
  getConfig: (id: string) => api.get<AppConfig>(`/apps/${id}/config`),

  /** Promote app to production (admin only) — BE issue #49 */
  promote: (id: string, comment?: string) =>
    api.post<AppPromotionResult>(`/apps/${id}/promote`, comment ? { comment } : {}),

  /** Return app from production back to dev (admin only) — BE issue #49 */
  demote: (id: string, comment?: string) =>
    api.post<AppPromotionResult>(`/apps/${id}/demote`, comment ? { comment } : {}),
}

// =============================================================================
// App environment promotion (BE issue #49)
// =============================================================================

/** One validation check evaluated by POST /apps/{id}/promote. */
export interface PromotionCheck {
  check: string
  passed: boolean
  message_en: string
  message_sk: string
}

/** Successful promote/demote response. */
export interface AppPromotionResult {
  app_id: string
  action: 'promote' | 'demote'
  from_environment: string
  to_environment: string
  checks: PromotionCheck[]
  app: AppDetail
}

/** 409 error detail returned when promotion validation fails. */
export interface PromotionBlockedDetail {
  message: string
  message_sk: string
  checks?: PromotionCheck[]
}

// =============================================================================
// App Configuration (issue #20 — stored config file)
// =============================================================================

/** GET /apps/{id}/config response. */
export interface AppConfig {
  app_id: string
  name: string
  /** Raw YAML exactly as stored in the registry */
  config_yaml?: string | null
  /** Parsed configuration */
  config_json?: Record<string, unknown> | null
  updated_at?: string | null
}

// =============================================================================
// Records Sync Health (issue #22, supersedes #20)
// =============================================================================
// GOVERNANCE: admins have no access to patient data. The BE exposes only
// aggregated counters per institution x app — no patient identifiers, no
// record IDs, no object-storage paths, no downloads.

/** Aggregated sync state of one institution x app combination. */
export interface SyncHealthItem {
  institution_id?: string | null
  institution_name?: string | null
  app_id?: string | null
  app_name?: string | null
  total_count: number
  pending_count: number
  uploaded_count: number
  failed_count: number
  /** Oldest started_at/created_at among pending records */
  oldest_pending_at?: string | null
  /** Currently always null — object sizes are not tracked in the platform DB */
  total_size_bytes?: number | null
}

export interface SyncHealthResponse {
  items: SyncHealthItem[]
  /** Sum of total_count over items */
  total_records: number
}

export interface SyncHealthParams {
  institution_id?: string
  app_id?: string
}

export const recordsApi = {
  /** Aggregated records sync health (per institution x app) */
  syncHealth: (params?: SyncHealthParams) =>
    api.get<SyncHealthResponse>('/records/', { params }),
}

// =============================================================================
// Health API (issue #19)
// =============================================================================

/** GET /health response (admin-portal-be issue #34 adds `version`). */
export interface HealthResponse {
  status: string
  /** Deployed BE version (APP_VERSION); absent on older backends. */
  version?: string
}

export const healthApi = {
  /**
   * Fetch BE health incl. deployed version.
   *
   * Unauthenticated and served at the backend root (outside /api/v1), so it
   * uses a plain axios call with a derived URL instead of the `api` instance.
   */
  get: () =>
    axios.get<HealthResponse>(deriveHealthUrl(API_BASE_URL), { timeout: 10000 }),
}

// =============================================================================
// Service accounts API (FE issue #28 / BE #64) — system_admin only
// =============================================================================
// Machine identities for automated monitoring (role "monitor"). Tokens have
// the form `rpsa_<prefix>_<secret>` and are returned ONLY by create/rotate;
// callers must never persist, log or route them. There is intentionally no
// delete endpoint (PROD rule: nothing is deleted) — deactivate instead.

export type ServiceAccountRole = 'monitor'

export interface ServiceAccount {
  id: string
  name: string
  description: string | null
  role: ServiceAccountRole
  is_active: boolean
  token_prefix: string
  created_at: string
  created_by: string | null
  rotated_at: string | null
  last_used_at: string | null
  last_used_ip: string | null
}

export interface ServiceAccountCreate {
  name: string
  description?: string | null
}

/** One-time response of create/rotate: the plaintext token is shown once. */
export interface ServiceAccountWithToken {
  account: ServiceAccount
  token: string
}

export const serviceAccountsApi = {
  list: () => api.get<ServiceAccount[]>('/service-accounts/'),

  create: (payload: ServiceAccountCreate) =>
    api.post<ServiceAccountWithToken>('/service-accounts/', payload),

  rotate: (id: string) =>
    api.post<ServiceAccountWithToken>(`/service-accounts/${encodeURIComponent(id)}/rotate`),

  deactivate: (id: string) =>
    api.post<ServiceAccount>(`/service-accounts/${encodeURIComponent(id)}/deactivate`),

  activate: (id: string) =>
    api.post<ServiceAccount>(`/service-accounts/${encodeURIComponent(id)}/activate`),
}
